import type { NotificationSound } from '@livechat/shared';

import type { ChatToast } from './chat-notifications.js';

export interface NotificationTestPreferences {
	newChatNotificationsEnabled: boolean;
	newMessageNotificationsEnabled: boolean;
}

export interface SystemNotificationResult {
	delivery?: 'page' | 'service-worker';
	status: 'failed' | 'permission-required' | 'shown' | 'unsupported';
}

export type VisualNotificationMethod = 'browser' | 'system';
export type NotificationSoundChoice = NotificationSound | 'none';

export function notificationSoundChoice(
	enabled: boolean,
	sound: NotificationSound,
): NotificationSoundChoice {
	return enabled ? sound : 'none';
}

export function notificationSoundPreference(choice: NotificationSoundChoice): {
	enabled: boolean;
	sound?: NotificationSound;
} {
	return choice === 'none' ? { enabled: false } : { enabled: true, sound: choice };
}

interface SoundNote {
	duration: number;
	frequency: number;
	offset: number;
	type: OscillatorType;
}

const soundPatterns: Record<NotificationSound, SoundNote[]> = {
	bell: [
		{ duration: 0.42, frequency: 880, offset: 0, type: 'sine' },
		{ duration: 0.5, frequency: 1320, offset: 0.04, type: 'sine' },
	],
	chime: [
		{ duration: 0.18, frequency: 659, offset: 0, type: 'sine' },
		{ duration: 0.24, frequency: 988, offset: 0.2, type: 'sine' },
	],
	pop: [{ duration: 0.14, frequency: 520, offset: 0, type: 'triangle' }],
};

const notificationOpenCallbacks = new Map<string, () => void>();
let listeningForNotificationClicks = false;

function notificationOptions(toast: ChatToast, url: string): NotificationOptions {
	return {
		body: toast.detail,
		data: {
			notificationId: toast.id,
			url,
		},
		tag: toast.id,
	};
}

function rememberNotificationOpen(toast: ChatToast, onOpen: () => void): void {
	notificationOpenCallbacks.set(toast.id, onOpen);
	window.setTimeout(() => notificationOpenCallbacks.delete(toast.id), 10 * 60 * 1_000);
}

function listenForNotificationClicks(): void {
	if (listeningForNotificationClicks || !('serviceWorker' in navigator)) return;
	listeningForNotificationClicks = true;
	navigator.serviceWorker.addEventListener('message', (event) => {
		const payload = event.data as { notificationId?: unknown; type?: unknown } | undefined;
		if (
			payload?.type !== 'livechat:system-notification-open' ||
			typeof payload.notificationId !== 'string'
		) {
			return;
		}
		const onOpen = notificationOpenCallbacks.get(payload.notificationId);
		notificationOpenCallbacks.delete(payload.notificationId);
		onOpen?.();
	});
}

async function notificationWorker(): Promise<ServiceWorkerRegistration | undefined> {
	if (!('serviceWorker' in navigator)) return undefined;
	try {
		listenForNotificationClicks();
		const existing = await navigator.serviceWorker.getRegistration('/');
		const registration =
			existing ??
			(await navigator.serviceWorker.register('/system-notification-worker.js', {
				scope: '/',
			}));
		return registration.active ? registration : await navigator.serviceWorker.ready;
	} catch {
		return undefined;
	}
}

export function notificationSoundPattern(sound: NotificationSound): SoundNote[] {
	return soundPatterns[sound];
}

export function notificationTestToasts(preferences: NotificationTestPreferences): ChatToast[] {
	const timestamp = Date.now();
	return [
		...(preferences.newChatNotificationsEnabled
			? [
					{
						conversationId: 'notification-test',
						detail: 'Test Website Visitor · Customer service',
						id: `notification-test-chat-${timestamp}`,
						kind: 'new-chat' as const,
						title: 'New customer waiting',
					},
				]
			: []),
		...(preferences.newMessageNotificationsEnabled
			? [
					{
						conversationId: 'notification-test',
						detail: 'Test Website Visitor: This is a notification test.',
						id: `notification-test-message-${timestamp}`,
						kind: 'new-message' as const,
						title: 'New message received',
					},
				]
			: []),
	];
}

export function visualNotificationMethod(
	systemNotificationsSelected: boolean,
): VisualNotificationMethod {
	return systemNotificationsSelected ? 'system' : 'browser';
}

export async function playNotificationSound(sound: NotificationSound): Promise<void> {
	const AudioContextConstructor = window.AudioContext;
	if (!AudioContextConstructor) return;
	const context = new AudioContextConstructor();
	try {
		if (context.state === 'suspended') await context.resume();
		const now = context.currentTime;
		let finishAt = now;
		for (const note of notificationSoundPattern(sound)) {
			const oscillator = context.createOscillator();
			const gain = context.createGain();
			const startsAt = now + note.offset;
			const endsAt = startsAt + note.duration;
			oscillator.frequency.setValueAtTime(note.frequency, startsAt);
			oscillator.type = note.type;
			gain.gain.setValueAtTime(0.0001, startsAt);
			gain.gain.exponentialRampToValueAtTime(0.16, startsAt + 0.015);
			gain.gain.exponentialRampToValueAtTime(0.0001, endsAt);
			oscillator.connect(gain);
			gain.connect(context.destination);
			oscillator.start(startsAt);
			oscillator.stop(endsAt);
			finishAt = Math.max(finishAt, endsAt);
		}
		window.setTimeout(() => void context.close(), (finishAt - now + 0.1) * 1_000);
	} catch {
		await context.close().catch(() => undefined);
	}
}

export function systemNotificationsSupported(): boolean {
	return 'Notification' in window;
}

export async function requestSystemNotificationPermission(): Promise<boolean> {
	if (!systemNotificationsSupported()) return false;
	if (Notification.permission === 'granted') return true;
	if (Notification.permission === 'denied') return false;
	try {
		return (await Notification.requestPermission()) === 'granted';
	} catch {
		return false;
	}
}

export async function showSystemNotification(
	toast: ChatToast,
	onOpen: () => void,
	url?: string,
): Promise<SystemNotificationResult> {
	if (!systemNotificationsSupported()) {
		return { status: 'unsupported' };
	}
	if (Notification.permission !== 'granted') {
		return { status: 'permission-required' };
	}
	const destinationUrl = url ?? window.location.href;

	const registration = await notificationWorker();
	if (registration) {
		try {
			rememberNotificationOpen(toast, onOpen);
			await registration.showNotification(
				toast.title,
				notificationOptions(toast, destinationUrl),
			);
			return { delivery: 'service-worker', status: 'shown' };
		} catch {
			notificationOpenCallbacks.delete(toast.id);
		}
	}

	try {
		const notification = new Notification(
			toast.title,
			notificationOptions(toast, destinationUrl),
		);
		notification.onclick = () => {
			notification.close();
			window.focus();
			onOpen();
		};
		return { delivery: 'page', status: 'shown' };
	} catch {
		return { status: 'failed' };
	}
}
